import { useRef, useState } from "react";
import { Text, XStack, YStack } from "tamagui";
import { kiosk } from "../kioskTheme";
import { Card } from "./ConfirmParts";
import PlateCameraSheet from "./PlateCameraSheet";

export interface PlatePhoto {
  url: string;
  file: File;
  /** 從這張照片解出的鐵牌條碼（解過就記著，供去重 & 刪照片時連帶移除比對）。 */
  barcode?: string;
  /** 該條碼比對到的既有客戶（cisId + 遮罩電話/地址）；沒比對到既有客戶則不設。 */
  cisId?: string;
  phone?: string;
  addr?: string;
}

interface PlateCardProps {
  plates: PlatePhoto[];
  setPlates: (v: PlatePhoto[]) => void;
  /** 鋼瓶掃描開關開啟時，開鏡頭 5 秒未拍會背景嘗試讀 barcode、拍完照片也再試讀一次。 */
  scanEnabled?: boolean;
  /** 讀到鐵牌 barcode（供上層打 cylinder-by-barcode 比對客戶）。 */
  onBarcode?: (code: string, sourceFile?: File) => void;
}

/**
 * 結算頁內嵌「鋼瓶鐵牌」拍照 / 上傳區塊（可拍多張、可略過，之後補拍）。
 * 新客戶首次結帳顯示；照片幫瓦斯行更快確認押金金額。
 * 「拍攝」開 in-app 相機（PlateCameraSheet，拍照優先＋條件式讀碼）；「從相簿選擇」維持檔案挑選。
 */
const PlateCard = ({ plates, setPlates, scanEnabled = false, onBarcode }: PlateCardProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      const added = files.map((f) => ({ url: URL.createObjectURL(f), file: f }));
      setPlates([...plates, ...added]);
    }
    e.target.value = ""; // 允許再次選同一張
  };

  const removeAt = (i: number) => {
    URL.revokeObjectURL(plates[i].url);
    setPlates(plates.filter((_, idx) => idx !== i));
  };

  const box: React.CSSProperties = {
    position: "relative",
    width: "100%",
    aspectRatio: "1 / 1",
    borderRadius: 12,
    overflow: "hidden",
  };

  return (
    <>
      <XStack ai="center" gap={8} mt={16} mb={8} ml={4}>
        <Text fontSize={13} fontWeight="800" color={kiosk.blueD} letterSpacing={0.8}>
          鋼瓶鐵牌
        </Text>
        <Text fontSize={11.5} fontWeight="800" color="#059669" backgroundColor="#ECFDF5" borderColor="#A7F3D0" borderWidth={1} borderRadius={999} px={8} py={1.5}>
          選填
        </Text>
      </XStack>
      <Card>
        <YStack py={4} gap={14}>
          <XStack gap={12} ai="center">
            <svg width="52" height="66" viewBox="0 0 56 70" fill="none">
              <rect x="23" y="2" width="10" height="7" rx="2" fill="#90a4c4" />
              <rect x="20" y="8" width="16" height="6" rx="2" fill="#b3c0d8" />
              <rect x="13" y="13" width="30" height="53" rx="11" fill="#2196f3" />
              <rect x="17" y="19" width="10" height="40" rx="5" fill="#fff" opacity="0.18" />
              <rect x="20" y="21" width="19" height="12" rx="2" fill="#fff" stroke="#0f5aa8" strokeWidth="1" />
              <line x1="23" y1="25" x2="36" y2="25" stroke="#9fb2c9" strokeWidth="1.4" />
              <line x1="23" y1="28.5" x2="33" y2="28.5" stroke="#9fb2c9" strokeWidth="1.4" />
              <rect x="16" y="17" width="27" height="20" rx="4" fill="none" stroke="#F59E0B" strokeWidth="1.6" strokeDasharray="3 2.5" />
            </svg>
            <YStack flex={1} gap={3}>
              <Text fontSize={15} fontWeight="800" color={kiosk.ink}>
                拍鋼瓶的鐵牌
              </Text>
              <Text fontSize={12.5} color={kiosk.grey} lineHeight={18}>
                <Text fontWeight="800" color="#059669">非必填</Text>；拍了鐵牌能讓瓦斯行更快確認押金金額。可拍/上傳多張，<Text fontWeight="700" color={kiosk.ink}>不拍也能直接下單</Text>。
              </Text>
            </YStack>
          </XStack>

          {/* 鐵牌位置示意：實拍照放在 public/images/plate-sample.jpg，讓客戶知道要拍的就是這塊金屬牌。 */}
          <XStack ai="center" gap={12} backgroundColor="#F0F7FF" borderColor="#DBEAFE" borderWidth={1} borderRadius={12} p={12}>
            <img
              src="/images/plate-sample.jpg"
              alt="鋼瓶鐵牌範例"
              style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 10, flexShrink: 0, border: "1px solid #DBEAFE", background: "#E8F1FF" }}
            />
            <YStack flex={1} gap={3} minWidth={0}>
              <Text fontSize={13.5} fontWeight="800" color={kiosk.ink}>
                鐵牌在鋼瓶上的金屬牌
              </Text>
              <Text fontSize={12} color={kiosk.grey} lineHeight={17}>
                鋼瓶上有一組號碼與條碼的金屬牌，對準它拍清楚即可（如圖）。
              </Text>
            </YStack>
          </XStack>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            {plates.map((p, i) => (
              <div key={p.url} style={box}>
                <img src={p.url} alt={`鐵牌 ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                <span
                  onClick={() => removeAt(i)}
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "rgba(15,26,53,.62)",
                    color: "#fff",
                    fontSize: 14,
                    lineHeight: "22px",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  ×
                </span>
              </div>
            ))}
            <button
              onClick={() => setCameraOpen(true)}
              style={{
                ...box,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                border: `1.5px dashed ${kiosk.line}`,
                background: "#fbfcfe",
                color: kiosk.blue,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 22, lineHeight: 1 }}>＋</span>
              <span style={{ fontSize: 12, fontWeight: 600 }}>拍攝</span>
            </button>
          </div>

          <XStack jc="space-between" ai="center">
            {plates.length > 0 ? (
              <Text fontSize={12.5} color={kiosk.grey}>
                已加入 {plates.length} 張
              </Text>
            ) : (
              <span />
            )}
            <Text fontSize={12.5} fontWeight="700" color={kiosk.blue} cursor="pointer" onPress={() => inputRef.current?.click()}>
              從相簿選擇
            </Text>
          </XStack>
        </YStack>
      </Card>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={onFiles}
      />

      <PlateCameraSheet
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => setPlates([...plates, { url: URL.createObjectURL(file), file }])}
        scanEnabled={scanEnabled}
        onBarcode={onBarcode}
      />
    </>
  );
};

export default PlateCard;
